/**
 * useEditorContextMenu - Hook for managing context menu and element highlighting in the editor
 */
import { useState, useCallback, useEffect, useMemo, useRef } from "react"
import { notify } from "shared/ui/Notifications/notify"
import { Editor as TiptapEditor } from "@tiptap/react"
import {
  EditorElement,
  detectElementAtLine,
  getLineNumberFromMousePosition,
  deleteElement,
  duplicateElement,
} from "utils/editorElementDetection"
import { htmlToText, textToHtml } from "utils/htmlTextConverter"
import { parseLabelsAndLists, LabelItem, LabelType } from "utils/labelUtils"
import { NodeType } from "utils/structureParser"
import { EditorRef } from "types/questionnaire.types"
import {
  ContextMenuAction,
  getContextMenuItems,
  MenuItem,
} from "features/QuestionnaireEditor/components/Editor/ContextMenu/contextMenuActions"

/**
 * Extended window interface for list picker state
 */
interface WindowWithListPicker extends Window {
  __listPickerIsReplace?: boolean
}

declare const window: WindowWithListPicker

interface UseEditorContextMenuOptions {
  /** Reference to the editor container */
  editorContainerRef: React.RefObject<HTMLDivElement | null>
  /** Reference to the editor instance */
  editor: TiptapEditor | null
  /** Reference to the editor ref object */
  editorRef: React.RefObject<EditorRef | null>
  /** Callback to open structure panel at specific line */
  onOpenStructurePanel?: (lineNumber: number) => void
  /** Callback to open labels modal and optionally navigate to a specific label */
  onOpenLabelsModal?: (labelName?: string) => void
  /** Callback when content changes */
  onContentChange?: () => void
  /** Callback to add a comment on the selected text */
  onAddComment?: () => void
}

interface ConfirmationModalState {
  isVisible: boolean
  title: string
  message: string
  confirmButtonText: string
  confirmButtonType: "primary" | "secondary" | "success" | "danger" | "default"
  onConfirm: () => void
  onCancel?: () => void // Optional custom cancel action (defaults to just closing modal)
  alternativeAction?: {
    text: string
    onClick: () => void
    buttonType?: "primary" | "secondary" | "success" | "danger" | "default"
  }
}

interface UsageInfo {
  lineNumber: number
  content: string
}

interface UsagesPanelState {
  isVisible: boolean
  listName: string
  usages: UsageInfo[]
}

interface ExceptionItemUsage {
  text: string
  definitionLine: number
  usageLines: number[]
  usageContents: string[]
}

interface ExceptionsUsagesPanelState {
  isVisible: boolean
  exceptionItems: ExceptionItemUsage[]
}

interface TimingModalState {
  isVisible: boolean
  elementType: "question" | "section"
  currentTiming?: number
  elementLineNumber: number
}

interface UseEditorContextMenuReturn {
  /** Currently highlighted element */
  highlightedElement: EditorElement | null
  /** Element for which context menu is shown */
  contextMenuElement: EditorElement | null
  /** Context menu position */
  contextMenuPosition: { x: number; y: number } | null
  /** Context menu items */
  contextMenuItems: MenuItem[]
  /** Handler for click (highlighting) */
  handleClick: (e: React.MouseEvent) => void
  /** Handler for context menu */
  handleContextMenu: (e: React.MouseEvent) => void
  /** Handler to close context menu */
  closeContextMenu: () => void
  /** Handler for menu item click */
  handleMenuClick: (actionKey: string) => void
  /** List picker modal state */
  listPickerState: {
    isVisible: boolean
    mode: "FROM" | "PER" | null
    onSelect: (list: LabelItem) => void
    onClose: () => void
  }
  /** Available lists for picker */
  availableLists: LabelItem[]
  /** Function to refresh highlighting */
  refreshHighlight: () => void
  /** Confirmation modal state */
  confirmationModal: ConfirmationModalState | null
  /** Close confirmation modal */
  closeConfirmationModal: () => void
  /** Usages panel state */
  usagesPanel: UsagesPanelState | null
  /** Close usages panel */
  closeUsagesPanel: () => void
  /** Exceptions usages panel state */
  exceptionsUsagesPanel: ExceptionsUsagesPanelState | null
  /** Close exceptions usages panel */
  closeExceptionsUsagesPanel: () => void
  /** Scroll to a specific line */
  scrollToLine: (lineNumber: number) => void
  /** Timing modal state */
  timingModal: TimingModalState | null
  /** Close timing modal */
  closeTimingModal: () => void
  /** Handle timing confirmation */
  handleTimingConfirm: (minutes: number) => void
}

export const useEditorContextMenu = (
  options: UseEditorContextMenuOptions
): UseEditorContextMenuReturn => {
  const {
    editorContainerRef,
    editor,
    editorRef,
    onOpenStructurePanel,
    onOpenLabelsModal,
    onContentChange,
    onAddComment,
  } = options

  // State
  const [highlightedElement, setHighlightedElement] =
    useState<EditorElement | null>(null)
  const [contextMenuElement, setContextMenuElement] =
    useState<EditorElement | null>(null)
  const [contextMenuPosition, setContextMenuPosition] = useState<{
    x: number
    y: number
  } | null>(null)
  const [listPickerMode, setListPickerMode] = useState<"FROM" | "PER" | null>(
    null
  )
  const [hasSelectedText, setHasSelectedText] = useState<boolean>(false)
  const [confirmationModal, setConfirmationModal] =
    useState<ConfirmationModalState | null>(null)
  const [usagesPanel, setUsagesPanel] = useState<UsagesPanelState | null>(null)
  const [exceptionsUsagesPanel, setExceptionsUsagesPanel] =
    useState<ExceptionsUsagesPanelState | null>(null)
  const [timingModal, setTimingModal] = useState<TimingModalState | null>(null)

  // Track mouse position for refresh
  const lastMousePositionRef = useRef<{ x: number; y: number } | null>(null)

  // Get current editor content
  const getCurrentContent = useCallback((): string => {
    // isDestroyed guard: tiptap v3 destroys/recreates the editor inside an
    // effect when its extensions change (every variant switch rebuilds them),
    // so effects registered later in the same commit can receive an editor
    // snapshot that was destroyed moments earlier — where getHTML() crashes
    // on the nulled schema ("can't access property 'cached'").
    if (!editor || editor.isDestroyed) return ""
    const html = editor.getHTML()
    return htmlToText(html)
  }, [editor])

  // Track editor content for reactivity
  const [editorContent, setEditorContent] = useState<string>("")

  // Update content when editor changes
  useEffect(() => {
    if (!editor || editor.isDestroyed) return

    const updateContent = () => {
      setEditorContent(getCurrentContent())
    }

    // Initial content
    updateContent()

    // Listen for updates
    editor.on("update", updateContent)
    return () => {
      editor.off("update", updateContent)
    }
  }, [editor, getCurrentContent])

  // Parse available lists from content
  const availableLists = useMemo((): LabelItem[] => {
    if (!editorContent) return []
    return parseLabelsAndLists(editorContent).filter(
      (item) =>
        item.type === LabelType.LIST || item.type === LabelType.RANGE_LIST
    )
  }, [editorContent])

  // Handle click for highlighting
  const handleClick = useCallback(
    (e: React.MouseEvent) => {
      // Don't interfere with right-click (context menu)
      if (e.button !== 0) return

      if (!editorContainerRef.current || !editor) return

      const lineNumber = getLineNumberFromMousePosition(
        e.clientY,
        editorContainerRef.current
      )

      if (lineNumber < 1) {
        setHighlightedElement(null)
        return
      }

      const content = getCurrentContent()
      const element = detectElementAtLine(
        lineNumber,
        content,
        editorContainerRef.current
      )

      setHighlightedElement(element)
    },
    [editorContainerRef, editor, getCurrentContent]
  )

  // Listen for undo/redo events to refresh highlighting
  useEffect(() => {
    // Same isDestroyed rationale as getCurrentContent above.
    if (!editor || editor.isDestroyed || !editorContainerRef.current) return

    const handleUpdate = () => {
      // Only refresh if we have a highlighted element and mouse position
      if (
        highlightedElement &&
        lastMousePositionRef.current &&
        editorContainerRef.current
      ) {
        const lineNumber = getLineNumberFromMousePosition(
          lastMousePositionRef.current.y,
          editorContainerRef.current
        )
        if (lineNumber > 0) {
          const content = getCurrentContent()
          const element = detectElementAtLine(
            lineNumber,
            content,
            editorContainerRef.current
          )
          setHighlightedElement(element)
        }
      }
    }

    // Listen to editor updates (which includes undo/redo)
    editor.on("update", handleUpdate)

    return () => {
      editor.off("update", handleUpdate)
    }
  }, [editor, editorContainerRef, highlightedElement, getCurrentContent])

  // Track mouse position
  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      lastMousePositionRef.current = { x: e.clientX, y: e.clientY }
    }

    document.addEventListener("mousemove", handleMouseMove)
    return () => {
      document.removeEventListener("mousemove", handleMouseMove)
    }
  }, [])

  // Handle right-click context menu
  const handleContextMenu = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault()
      e.stopPropagation()

      if (!editorContainerRef.current || !editor) return

      const lineNumber = getLineNumberFromMousePosition(
        e.clientY,
        editorContainerRef.current
      )
      const content = getCurrentContent()
      const element =
        lineNumber > 0
          ? detectElementAtLine(lineNumber, content, editorContainerRef.current)
          : null

      // Check if there's selected text in the editor
      const { from, to } = editor.state.selection
      const isTextSelected = from !== to
      setHasSelectedText(isTextSelected)

      setContextMenuElement(element)
      setHighlightedElement(element) // Also highlight the element
      setContextMenuPosition({ x: e.clientX, y: e.clientY })
    },
    [editorContainerRef, editor, getCurrentContent]
  )

  // Close context menu
  const closeContextMenu = useCallback(() => {
    setContextMenuPosition(null)
    setContextMenuElement(null)
  }, [])

  // Close confirmation modal
  const closeConfirmationModal = useCallback(() => {
    setConfirmationModal(null)
  }, [])

  // Close usages panel
  const closeUsagesPanel = useCallback(() => {
    setUsagesPanel(null)
  }, [])

  // Close exceptions usages panel
  const closeExceptionsUsagesPanel = useCallback(() => {
    setExceptionsUsagesPanel(null)
  }, [])

  // Close timing modal
  const closeTimingModal = useCallback(() => {
    setTimingModal(null)
  }, [])

  // Handle set timing action
  const handleSetTiming = useCallback(
    (element: EditorElement) => {
      const elementType =
        element.type === NodeType.SECTION ? "section" : "question"

      // Check if element already has a timing property
      const content = getCurrentContent()
      const lines = content.split("\n")
      let currentTiming: number | undefined

      // Look for existing [[timing:X]] on the line above the element
      if (element.lineNumber > 1) {
        const lineAbove = lines[element.lineNumber - 2]?.trim()
        const timingMatch = lineAbove?.match(/^\[\[timing:(\d+)\]\]$/i)
        if (timingMatch) {
          currentTiming = parseInt(timingMatch[1], 10)
        }
      }

      setTimingModal({
        isVisible: true,
        elementType,
        currentTiming,
        elementLineNumber: element.lineNumber,
      })
      closeContextMenu()
    },
    [getCurrentContent, closeContextMenu]
  )

  // Update editor content
  const updateEditorContent = useCallback(
    (newContent: string) => {
      if (!editorRef.current) return
      const newHTML = textToHtml(newContent)
      editorRef.current.setHTML(newHTML)
      onContentChange?.()
    },
    [editorRef, onContentChange]
  )

  // Handle timing confirmation
  const handleTimingConfirm = useCallback(
    (minutes: number) => {
      if (!timingModal) return

      const content = getCurrentContent()
      const lines = content.split("\n")
      const elementLineIndex = timingModal.elementLineNumber - 1
      const timingLine = `[[timing:${minutes}]]`

      // Check if there's already a timing property above the element
      let hasExistingTiming = false
      let existingTimingLineIndex = -1

      if (elementLineIndex > 0) {
        const lineAbove = lines[elementLineIndex - 1]?.trim()
        if (lineAbove?.match(/^\[\[timing:\d+\]\]$/i)) {
          hasExistingTiming = true
          existingTimingLineIndex = elementLineIndex - 1
        }
      }

      let newLines: string[]
      if (hasExistingTiming) {
        // Replace existing timing
        newLines = [...lines]
        newLines[existingTimingLineIndex] = timingLine
      } else {
        // Insert new timing line above the element
        newLines = [
          ...lines.slice(0, elementLineIndex),
          timingLine,
          ...lines.slice(elementLineIndex),
        ]
      }

      updateEditorContent(newLines.join("\n"))
      notify.toast.success(`Timing set to ${minutes} minute${minutes > 1 ? "s" : ""}`)
      closeTimingModal()
    },
    [timingModal, getCurrentContent, updateEditorContent, closeTimingModal]
  )

  // Handle delete action
  const handleDelete = useCallback(
    (element: EditorElement) => {
      const usageCount = element.metadata?.referenceCount || 0

      const performDelete = () => {
        const currentContent = getCurrentContent()
        const newContent = deleteElement(element, currentContent)
        updateEditorContent(newContent)
        notify.toast.success(`${element.type} deleted successfully`)
        setHighlightedElement(null) // Clear highlight after delete
        closeContextMenu()
        closeConfirmationModal()
      }

      if (
        (element.type === NodeType.LIST ||
          element.type === NodeType.RANGE_LIST) &&
        usageCount > 0
      ) {
        setConfirmationModal({
          isVisible: true,
          title: `Delete "${element.name}"?`,
          message: `This list is used in ${usageCount} question(s). Deleting it may cause errors.`,
          confirmButtonText: "Delete Anyway",
          confirmButtonType: "danger",
          onConfirm: performDelete,
        })
      } else {
        setConfirmationModal({
          isVisible: true,
          title: `Delete ${element.type}?`,
          message: `Are you sure you want to delete "${element.name}"?`,
          confirmButtonText: "Delete",
          confirmButtonType: "danger",
          onConfirm: performDelete,
        })
      }
    },
    [
      getCurrentContent,
      updateEditorContent,
      closeContextMenu,
      closeConfirmationModal,
    ]
  )

  // Handle duplicate action
  const handleDuplicate = useCallback(
    (element: EditorElement) => {
      const currentContent = getCurrentContent()
      const newContent = duplicateElement(element, currentContent)
      updateEditorContent(newContent)
      notify.toast.success(`${element.type} duplicated successfully`)
      closeContextMenu()
    },
    [getCurrentContent, updateEditorContent, closeContextMenu]
  )

  // Handle insert/replace list (FROM or PER)
  const handleInsertList = useCallback(
    (
      element: EditorElement,
      mode: "FROM" | "PER",
      isReplace: boolean = false
    ) => {
      setListPickerMode(mode)
      // Store whether this is a replace operation
      window.__listPickerIsReplace = isReplace
      // Don't close context menu yet - will close after list selection
    },
    []
  )

  // Refresh highlighted element after content change
  const refreshHighlightedElement = useCallback(() => {
    if (!contextMenuElement || !editorContainerRef.current || !editor) return

    // Wait for content to update, then refresh the element detection
    setTimeout(() => {
      const content = getCurrentContent()
      const element = detectElementAtLine(
        contextMenuElement.lineNumber,
        content,
        editorContainerRef.current!
      )
      setHighlightedElement(element)
    }, 100)
  }, [contextMenuElement, editorContainerRef, editor, getCurrentContent])

  // Handle list selection from picker
  const handleListSelect = useCallback(
    (list: LabelItem) => {
      if (!contextMenuElement || !listPickerMode) return

      const isReplace = window.__listPickerIsReplace || false
      const currentContent = getCurrentContent()
      const lines = currentContent.split("\n")
      const questionLineIndex = contextMenuElement.lineNumber - 1

      if (listPickerMode === "FROM") {
        // Handle FROM list insertion/replacement
        if (isReplace) {
          // Find existing FROM list and all its options
          let selectLineIndex = -1
          let fromEndLineIndex = -1

          for (
            let i = questionLineIndex + 1;
            i < contextMenuElement.endLineNumber;
            i++
          ) {
            const line = lines[i].trim()
            if (line.match(/^SELECT\s+(ONE|MULTIPLE)\s+FROM\b/i)) {
              selectLineIndex = i
              // Find where FROM section ends (before PER or another SELECT keyword)
              fromEndLineIndex = i + 1
              for (let j = i + 1; j < contextMenuElement.endLineNumber; j++) {
                const nextLine = lines[j].trim()
                if (nextLine.match(/^(PER|SELECT|GRID)\b/i)) {
                  break
                }
                if (nextLine) {
                  fromEndLineIndex = j
                }
              }
              break
            }
          }

          if (selectLineIndex !== -1) {
            // Ask user if they want to replace all options or just append
            setConfirmationModal({
              isVisible: true,
              title: "Replace FROM list",
              message:
                "Do you want to replace all existing options with the list reference, or keep existing options and add the list?",
              confirmButtonText: "Replace All Options",
              confirmButtonType: "primary",
              onConfirm: () => {
                // Replace: Remove all FROM options and replace with list reference
                const newLines = [
                  ...lines.slice(0, selectLineIndex + 1),
                  list.name,
                  ...lines.slice(fromEndLineIndex + 1),
                ]
                updateEditorContent(newLines.join("\n"))
                notify.toast.success(`Replaced FROM options with ${list.name}`)
                refreshHighlightedElement()
                setListPickerMode(null)
                closeContextMenu()
                closeConfirmationModal()
              },
              alternativeAction: {
                text: "Keep & Add List",
                buttonType: "primary",
                onClick: () => {
                  // Append: Add list reference after existing options
                  const newLines = [
                    ...lines.slice(0, fromEndLineIndex + 1),
                    list.name,
                    ...lines.slice(fromEndLineIndex + 1),
                  ]
                  updateEditorContent(newLines.join("\n"))
                  notify.toast.success(`Added ${list.name} to existing options`)
                  refreshHighlightedElement()
                  setListPickerMode(null)
                  closeContextMenu()
                  closeConfirmationModal()
                },
              },
            })
            return
          }
        } else {
          // Insert new FROM list
          const hasSelectOptions = lines
            .slice(questionLineIndex + 1, contextMenuElement.endLineNumber)
            .some((line) => line.trim().match(/^(SELECT|GRID)/))

          if (hasSelectOptions) {
            setConfirmationModal({
              isVisible: true,
              title: "Insert List",
              message:
                "This question already has SELECT options. Do you want to replace them with the list reference, or keep both?",
              confirmButtonText: "Replace Options",
              confirmButtonType: "primary",
              onConfirm: () => {
                const newLines = [
                  ...lines.slice(0, questionLineIndex + 1),
                  "SELECT ONE FROM",
                  list.name,
                  ...lines.slice(contextMenuElement.endLineNumber),
                ]
                updateEditorContent(newLines.join("\n"))
                notify.toast.success(`Replaced options with FROM ${list.name}`)
                refreshHighlightedElement()
                setListPickerMode(null)
                closeContextMenu()
                closeConfirmationModal()
              },
              alternativeAction: {
                text: "Keep Both",
                buttonType: "primary",
                onClick: () => {
                  const lastLineIndex = contextMenuElement.endLineNumber - 1
                  const newLines = [
                    ...lines.slice(0, lastLineIndex + 1),
                    list.name,
                    ...lines.slice(lastLineIndex + 1),
                  ]
                  updateEditorContent(newLines.join("\n"))
                  notify.toast.success(`Added ${list.name} to existing options`)
                  refreshHighlightedElement()
                  setListPickerMode(null)
                  closeContextMenu()
                  closeConfirmationModal()
                },
              },
            })
            return
          } else {
            const newLines = [
              ...lines.slice(0, questionLineIndex + 1),
              "SELECT ONE FROM",
              list.name,
              ...lines.slice(questionLineIndex + 1),
            ]
            updateEditorContent(newLines.join("\n"))
            notify.toast.success(`Added FROM ${list.name}`)
            refreshHighlightedElement()
          }
        }
      } else {
        // Handle PER list insertion/replacement
        if (isReplace) {
          // Find existing PER list and all its rows
          let perLineIndex = -1
          let perEndLineIndex = -1

          for (
            let i = questionLineIndex + 1;
            i < contextMenuElement.endLineNumber;
            i++
          ) {
            const line = lines[i].trim()
            if (line.match(/^PER\b/i)) {
              perLineIndex = i
              // Find where PER section ends (before SELECT keyword)
              perEndLineIndex = i + 1
              // Skip [[RANDOMIZE]] if present
              let startJ = i + 1
              if (lines[startJ]?.trim().match(/^\[\[RANDOMIZE\]\]/i)) {
                perEndLineIndex = startJ
                startJ = startJ + 1
              }
              // Find all PER rows until SELECT
              for (let j = startJ; j < contextMenuElement.endLineNumber; j++) {
                const nextLine = lines[j].trim()
                if (nextLine.match(/^SELECT\b/i)) {
                  break
                }
                if (nextLine) {
                  perEndLineIndex = j
                }
              }
              break
            }
          }

          if (perLineIndex !== -1) {
            // Check if there's a [[RANDOMIZE]] directive
            const hasRandomize = lines[perLineIndex + 1]
              ?.trim()
              .match(/^\[\[RANDOMIZE\]\]/i)

            // Ask user if they want to replace all rows or just append
            setConfirmationModal({
              isVisible: true,
              title: "Replace PER list",
              message:
                "Do you want to replace all existing rows with the list reference, or keep existing rows and add the list?",
              confirmButtonText: "Replace All Rows",
              confirmButtonType: "primary",
              onConfirm: () => {
                // Replace: Remove all PER rows and replace with list reference
                const newLines = [
                  ...lines.slice(0, perLineIndex + 1),
                  ...(hasRandomize ? [lines[perLineIndex + 1]] : []), // Keep [[RANDOMIZE]] if present
                  list.name,
                  ...lines.slice(perEndLineIndex + 1),
                ]
                updateEditorContent(newLines.join("\n"))
                notify.toast.success(`Replaced PER rows with ${list.name}`)
                refreshHighlightedElement()
                setListPickerMode(null)
                closeContextMenu()
                closeConfirmationModal()
              },
              alternativeAction: {
                text: "Keep & Add List",
                buttonType: "primary",
                onClick: () => {
                  // Append: Add list reference after existing rows
                  const newLines = [
                    ...lines.slice(0, perEndLineIndex + 1),
                    list.name,
                    ...lines.slice(perEndLineIndex + 1),
                  ]
                  updateEditorContent(newLines.join("\n"))
                  notify.toast.success(`Added ${list.name} to existing rows`)
                  refreshHighlightedElement()
                  setListPickerMode(null)
                  closeContextMenu()
                  closeConfirmationModal()
                },
              },
            })
            return
          }
        } else {
          // Insert new PER list - add after question line
          const newLines = [
            ...lines.slice(0, questionLineIndex + 1),
            "PER",
            list.name,
            ...lines.slice(questionLineIndex + 1),
          ]
          updateEditorContent(newLines.join("\n"))
          notify.toast.success(`Added PER ${list.name}`)
          refreshHighlightedElement()
        }
      }

      setListPickerMode(null)
      closeContextMenu()
      delete window.__listPickerIsReplace
    },
    [
      contextMenuElement,
      listPickerMode,
      getCurrentContent,
      updateEditorContent,
      closeContextMenu,
      closeConfirmationModal,
      refreshHighlightedElement,
    ]
  )

  // Handle jump to structure
  const handleJumpToStructure = useCallback(
    (element: EditorElement) => {
      onOpenStructurePanel?.(element.lineNumber)
      closeContextMenu()
    },
    [onOpenStructurePanel, closeContextMenu]
  )

  // Handle jump to labels
  const handleJumpToLabels = useCallback(
    (element: EditorElement) => {
      onOpenLabelsModal?.(element.name)
      closeContextMenu()
    },
    [onOpenLabelsModal, closeContextMenu]
  )

  // Handle show usages
  const handleShowUsages = useCallback(
    (element: EditorElement) => {
      const content = getCurrentContent()
      const allLabels = parseLabelsAndLists(content)
      const targetList = allLabels.find((l) => l.name === element.name)

      if (!targetList) {
        notify.toast.info(`List "${element.name}" not found`)
        closeContextMenu()
        return
      }

      // Build usages array with both definition and references
      const usages: UsageInfo[] = []

      // Add definition line first (where the list is defined)
      if (targetList.lineNumbers && targetList.lineNumbers.length > 0) {
        const lines = content.split("\n")
        usages.push({
          lineNumber: targetList.lineNumbers[0],
          content: lines[targetList.lineNumbers[0] - 1] || "",
        })
      }

      // Add all reference lines (where the list is used)
      if (
        targetList.referenceLineNumbers &&
        targetList.referenceLineNumbers.length > 0
      ) {
        targetList.referenceLineNumbers.forEach((lineNum, idx) => {
          usages.push({
            lineNumber: lineNum,
            content: targetList.referenceLines?.[idx] || "",
          })
        })
      }

      if (usages.length === 0) {
        notify.toast.info(`No usages found for "${element.name}"`)
        closeContextMenu()
        return
      }

      setUsagesPanel({
        isVisible: true,
        listName: element.name,
        usages,
      })

      closeContextMenu()
    },
    [getCurrentContent, closeContextMenu]
  )

  // Handle show exceptions usages
  const handleShowExceptionsUsages = useCallback(
    (_element: EditorElement) => {
      const content = getCurrentContent()
      const lines = content.split("\n")
      const allLabels = parseLabelsAndLists(content)
      const exceptionsLabel = allLabels.find(
        (l) => l.type === LabelType.EXCEPTIONS
      )

      if (!exceptionsLabel) {
        notify.toast.info("No exceptions block found")
        closeContextMenu()
        return
      }

      // Get the exceptions definition line
      const definitionLine = exceptionsLabel.lineNumbers[0]

      // Extract individual exception phrases from the exceptions block
      // The originalLines contains all exception items (first line is "EXCEPTIONS", rest are items)
      const exceptionPhrases = exceptionsLabel.originalLines
        .slice(1)
        .map((line) => line.trim())
        .filter(Boolean)

      if (exceptionPhrases.length === 0) {
        notify.toast.info("No exception items found")
        closeContextMenu()
        return
      }

      // Build usage data for each exception phrase
      const exceptionItems: ExceptionItemUsage[] = exceptionPhrases.map(
        (phrase, index) => {
          // Find all lines where this exact phrase appears (excluding the definition)
          const usageLines: number[] = []
          const usageContents: string[] = []

          lines.forEach((line, lineIndex) => {
            const trimmedLine = line.trim()
            const lineNumber = lineIndex + 1

            // Skip the exceptions definition block itself
            if (
              lineNumber >= definitionLine &&
              lineNumber < definitionLine + exceptionPhrases.length + 1
            ) {
              return
            }

            // Check if this line contains the exact phrase
            if (trimmedLine === phrase) {
              usageLines.push(lineNumber)
              usageContents.push(line)
            }
          })

          return {
            text: phrase,
            definitionLine: definitionLine + index + 1, // +1 for the "EXCEPTIONS" line, +index for this specific item
            usageLines,
            usageContents,
          }
        }
      )

      setExceptionsUsagesPanel({
        isVisible: true,
        exceptionItems,
      })

      closeContextMenu()
    },
    [getCurrentContent, closeContextMenu]
  )

  // Scroll to line function
  const scrollToLine = useCallback(
    (lineNumber: number) => {
      if (!editorContainerRef.current) return

      const editorContent =
        editorContainerRef.current.querySelector(".ProseMirror")
      if (!editorContent) return

      const lines = editorContent.querySelectorAll(
        ".ProseMirror > *"
      ) as NodeListOf<HTMLElement>
      if (lineNumber < 1 || lineNumber > lines.length) return

      const targetLine = lines[lineNumber - 1]
      if (!targetLine) return

      // Scroll to the line
      targetLine.scrollIntoView({ behavior: "smooth", block: "center" })

      // Highlight the element by simulating a click
      setTimeout(() => {
        // Get the bounding rect to simulate a click in the center of the line
        const rect = targetLine.getBoundingClientRect()
        const centerX = rect.left + rect.width / 2
        const centerY = rect.top + rect.height / 2

        // Create and dispatch a click event at the center of the line
        const clickEvent = new MouseEvent("click", {
          bubbles: true,
          cancelable: true,
          clientX: centerX,
          clientY: centerY,
          view: window,
        })

        targetLine.dispatchEvent(clickEvent)
      }, 500)
    },
    [editorContainerRef]
  )

  // Handle menu item click
  const handleMenuClick = useCallback(
    (actionKey: string) => {
      // Handle ADD_COMMENT even without contextMenuElement (only needs selected text)
      if (actionKey === ContextMenuAction.ADD_COMMENT) {
        onAddComment?.()
        closeContextMenu()
        return
      }

      // TOGGLE_REMARK works on whatever line(s) the user invoked the menu over,
      // even when there is no element (empty space) and no text selection.
      if (actionKey === ContextMenuAction.TOGGLE_REMARK) {
        if (editor) {
          const { from, to } = editor.state.selection
          const hasSelection = from !== to

          // If no text selection, retarget the cursor to the right-clicked line
          // so the toggle hits that paragraph rather than wherever focus was.
          if (!hasSelection && contextMenuElement) {
            const targetLine = contextMenuElement.lineNumber
            let paragraphIndex = 0
            let targetPos: number | null = null
            editor.state.doc.descendants((node, pos) => {
              if (node.type.name === "paragraph") {
                paragraphIndex += 1
                if (paragraphIndex === targetLine) {
                  targetPos = pos + 1
                  return false
                }
                return false
              }
              return true
            })
            if (targetPos !== null) {
              editor
                .chain()
                .focus()
                .setTextSelection(targetPos)
                .toggleRemarks()
                .run()
            } else {
              editor.chain().focus().toggleRemarks().run()
            }
          } else {
            editor.chain().focus().toggleRemarks().run()
          }
        }
        closeContextMenu()
        return
      }

      if (!contextMenuElement) {
        closeContextMenu()
        return
      }

      switch (actionKey) {
        case ContextMenuAction.DELETE:
          handleDelete(contextMenuElement)
          break
        case ContextMenuAction.DUPLICATE:
          handleDuplicate(contextMenuElement)
          break
        case ContextMenuAction.INSERT_LIST_FROM:
          handleInsertList(contextMenuElement, "FROM", false)
          break
        case ContextMenuAction.INSERT_LIST_PER:
          handleInsertList(contextMenuElement, "PER", false)
          break
        case ContextMenuAction.REPLACE_LIST_FROM:
          handleInsertList(contextMenuElement, "FROM", true)
          break
        case ContextMenuAction.REPLACE_LIST_PER:
          handleInsertList(contextMenuElement, "PER", true)
          break
        case ContextMenuAction.JUMP_TO_STRUCTURE:
          handleJumpToStructure(contextMenuElement)
          break
        case ContextMenuAction.JUMP_TO_LABELS:
          handleJumpToLabels(contextMenuElement)
          break
        case ContextMenuAction.SHOW_USAGES:
          // Check if this is an EXCEPTIONS element
          if (contextMenuElement.type === NodeType.EXCEPTIONS) {
            handleShowExceptionsUsages(contextMenuElement)
          } else {
            handleShowUsages(contextMenuElement)
          }
          break
        case ContextMenuAction.SET_TIMING:
          handleSetTiming(contextMenuElement)
          break
        default:
          // For unimplemented actions
          notify.toast.info(`${actionKey} - Coming soon!`)
          closeContextMenu()
      }
    },
    [
      contextMenuElement,
      editor,
      handleDelete,
      handleDuplicate,
      handleInsertList,
      handleJumpToStructure,
      handleJumpToLabels,
      handleShowUsages,
      handleShowExceptionsUsages,
      handleSetTiming,
      closeContextMenu,
      onAddComment,
    ]
  )

  // Generate context menu items
  const contextMenuItems = useMemo(() => {
    return getContextMenuItems(contextMenuElement, false, hasSelectedText)
  }, [contextMenuElement, hasSelectedText])

  return {
    highlightedElement,
    contextMenuElement,
    contextMenuPosition,
    contextMenuItems,
    handleClick,
    handleContextMenu,
    closeContextMenu,
    handleMenuClick,
    listPickerState: {
      isVisible: listPickerMode !== null,
      mode: listPickerMode,
      onSelect: handleListSelect,
      onClose: () => {
        setListPickerMode(null)
        closeContextMenu()
      },
    },
    availableLists,
    refreshHighlight: refreshHighlightedElement,
    confirmationModal,
    closeConfirmationModal,
    usagesPanel,
    closeUsagesPanel,
    exceptionsUsagesPanel,
    closeExceptionsUsagesPanel,
    scrollToLine,
    timingModal,
    closeTimingModal,
    handleTimingConfirm,
  }
}
